// React
import { useEffect, useState } from "react";
import {
    Link as ReactRouterLink,
    useNavigate,
    useParams,
} from "react-router-dom";
import React from "react";

// Hooks and Redux
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { refreshMasterData } from "../../../redux/slices/masterDataSlice"

// MUI Components
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';

// MUI Icons
import HomeIcon from '@mui/icons-material/Home';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';

// Utils
import { ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from 'yup';
import jwtDecode from "jwt-decode";
import Swal from "sweetalert2";

// Styles
import { green } from "@mui/material/colors";

// Types
import { TokenPayload } from "../../../@types";

// Services
import {
    createReligion,
    updateReligion,
    getReligionById,
} from "../../../services/MasterDataService";

interface ReligionFormData {
    external_code: string;
    label_th: string;
    label_en: string;
    is_active: boolean;
}

type ModeType = "add" | "edit"

interface MyEditorProps {
  type: ModeType
  editId?: string
}

const ManageReligion: React.FC<MyEditorProps> = ({ type = "add", editId }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams(); // external_code when editing; undefined when creating

    // Auto-detect mode from URL params
    const isEdit = Boolean(id);

    const dispatch = useAppDispatch()

  const userRoleType = useAppSelector((state) => state.user.roletype);
    const havePermission = userRoleType === 'Super Admin';

    const [formData, setFormData] = useState<ReligionFormData>({
        external_code: '',
        label_th: '',
        label_en: '',
        is_active: true,
    });

    const [dataLoading, setDataLoading] = useState<boolean>(isEdit);
    const [submitLoading, setSubmitLoading] = useState<boolean>(false);

    if (!havePermission) {
        navigate('/forbidden', { replace: true });
    }

    // ── Fetch existing religion data when editing ──────────────────────────────
    useEffect(() => {
        if (!isEdit || !id) return;

        const fetchReligion = async () => {
            try {
                setDataLoading(true);
                const response = await getReligionById(id);

                if (response.data?.success === false) {
                    Swal.fire({
                        title: i18n.language === 'th' ? response.data?.message_th : response.data?.message_en,
                        icon: 'error',
                        showCancelButton: false,
                        confirmButtonColor: '#43A047',
                        confirmButtonText: 'OK!',
                        allowOutsideClick: false,
                        allowEscapeKey: false,
                    }).then((result) => {
                        if (result.isConfirmed) {
                            dispatch(refreshMasterData())
              navigate('/admin/masterdata/religion', { replace: true });
                        }
                    });
                    return;
                }

                const religion = response.data?.religion;
                setFormData({
                    external_code: religion.external_code,
                    label_en: religion.label_en,
                    label_th: religion.label_th,
                    is_active: religion.is_active,
                });
            } catch (error) {
                navigate('/servererror', { replace: true });
            } finally {
                setDataLoading(false);
            }
        };

        fetchReligion();
    }, [id, isEdit, navigate, i18n.language]);

    // ── Validation ─────────────────────────────────────────────────────────────
    const formValidationSchema = Yup.object().shape({
        ...(isEdit
            ? {}
            : { external_code: Yup.string().required(`${t("External code is required")}`)
                .min(3, `${t("External code must be at least 3 characters")}`)
            .max(3, `${t("External code must be at most 3 characters")}`), }),
        label_en: Yup.string().required(`${t("label_en is required")}`),
        label_th: Yup.string().required(`${t("label_th is required")}`),
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({ resolver: yupResolver(formValidationSchema) });

    const handleActiveChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({ ...prev, is_active: event.target.checked }));
    };

    // ── Submit ─────────────────────────────────────────────────────────────────
    const onSubmit = (values: any) => {
        const submit = async () => {
            try {
                const parsedToken: TokenPayload = jwtDecode(localStorage.access_token);
                setSubmitLoading(true);

                let response;

                if (isEdit) {
                    const editData = {
                        external_code: formData.external_code,
                        label_en: values.label_en,
                        label_th: values.labe_th,
                        is_active: formData.is_active,
                        modified_by: parsedToken.Username,
                    };
                    response = await updateReligion(id as string, editData);
                } else {
                    const createData = {
                        external_code: values.external_code,
                        label_en: values.label_en,
                        label_th: values.label_th,
                        is_active: formData.is_active,
                        created_by: parsedToken.Username,
                    };
                    response = await createReligion(createData);
                }

                if (response) {
                    setSubmitLoading(false);
                    const responseData = response.data;

                    if (!responseData?.success) {
                        toast.error(
                            i18n.language === 'th' ? responseData.message_th : responseData.message_en,
                            {
                                position: "top-center",
                                autoClose: 3000,
                                hideProgressBar: false,
                                closeOnClick: true,
                                pauseOnHover: false,
                                draggable: true,
                                theme: "light",
                            }
                        );
                    } else {
                        Swal.fire({
                            title: i18n.language === 'th' ? responseData.message_th : responseData.message_en,
                            icon: 'success',
                            showCancelButton: false,
                            confirmButtonColor: '#43A047',
                            confirmButtonText: 'OK!',
                            allowOutsideClick: false,
                            allowEscapeKey: false,
                        }).then((result) => {
                            if (result.isConfirmed) {
                                dispatch(refreshMasterData())
              navigate('/admin/masterdata/religion', { replace: true });
                            }
                        });
                    }
                }
            } catch (error) {
                navigate('/servererror', { replace: true });
            }
        };

        submit();
    };

    // ── Loading skeleton (edit mode fetching data) ─────────────────────────────
    if (dataLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
      <>
        <ToastContainer style={{ width: "400px" }} />

        {havePermission && (
          <>
            {/* ── Back + Breadcrumbs ─────────────────────────────────────── */}
            <Box sx={{ height: "auto", maxWidth: "100%", mt: 2 }}>
              <Box display="flex" gap={3} sx={{ mb: 3 }}>
                <Box
                  display="flex"
                  component={ReactRouterLink}
                  onClick={() => window.history.back()}
                  sx={{
                    textDecoration: "none",
                    color: "#0d47a1",
                    alignItems: "center",
                    ":hover": { color: "#1976d2" },
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                  <Typography sx={{ fontWeight: "bold" }}>
                    {t("Back")}
                  </Typography>
                </Box>

                <Breadcrumbs aria-label="breadcrumb">
                  <Link
                    underline="hover"
                    sx={{ display: "flex", alignItems: "center" }}
                    color="inherit"
                    component={ReactRouterLink}
                    to="/admin"
                  >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    {t("Dashboard")}
                  </Link>
                  <Link
                    underline="hover"
                    sx={{ display: "flex", alignItems: "center" }}
                    color="inherit"
                    component={ReactRouterLink}
                    to="/admin/masterdata/religion"
                  >
                    <TempleHinduIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    {t("Religion")}
                  </Link>
                  <Typography
                    sx={{ display: "flex", alignItems: "center" }}
                    color="text.primary"
                  >
                    {isEdit ? (
                      <>
                        <EditIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        {t("Edit Religion")}
                      </>
                    ) : (
                      <>
                        <AddCircleOutlineIcon
                          sx={{ mr: 0.5 }}
                          fontSize="inherit"
                        />
                        {t("Create New Religion")}
                      </>
                    )}
                  </Typography>
                </Breadcrumbs>
              </Box>
            </Box>

            {/* ── Page title ────────────────────────────────────────────── */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                height: "70px",
              }}
            >
              <Typography
                variant="h4"
                sx={{ fontWeight: "bold", textAlign: "left" }}
              >
                {isEdit ? t("Edit Religion") : t("Create New Religion")}
              </Typography>
            </Box>

            {/* ── Form ──────────────────────────────────────────────────── */}
            <Box
              component="form"
              noValidate
              sx={{ mt: 3 }}
              onKeyDown={(e: React.KeyboardEvent) =>
                e.key === "Enter" && e.preventDefault()
              }
            >
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="externalCode"
                    label={t("External Code")}
                    disabled={isEdit}
                    {...(!isEdit ? register("external_code") : {})}
                    error={!!errors.external_code}
                    value={isEdit ? formData.external_code : undefined}
                    onChange={
                      isEdit
                        ? undefined
                        : (event: React.ChangeEvent<HTMLInputElement>) => {
                            setFormData((prev) => ({
                              ...prev,
                              external_code: event.target.value,
                            }))
                            ;(errors as any).external_code = undefined
                          }
                    }
                  />
                  <Typography
                    variant="inherit"
                    color="error"
                    align="left"
                    pt={1}
                    fontSize={13}
                  >
                    {errors.external_code?.message}
                  </Typography>
                </Grid>

                {/* label_en */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="label_en"
                    label={t("label_en")}
                    {...register("label_en")}
                    error={!!errors.label_en}
                    value={formData.label_en}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setFormData((prev) => ({
                        ...prev,
                        label_en: event.target.value,
                      }))
                      ;(errors as any).label = undefined
                    }}
                  />
                  <Typography
                    variant="inherit"
                    color="error"
                    align="left"
                    pt={1}
                    fontSize={13}
                  >
                    {errors.label_en?.message}
                  </Typography>
                </Grid>

                {/* label_th */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="label_th"
                    label={t("label_th")}
                    {...register("label_th")}
                    error={!!errors.label_th}
                    value={formData.label_th}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setFormData((prev) => ({
                        ...prev,
                        label_th: event.target.value,
                      }))
                      ;(errors as any).label = undefined
                    }}
                  />
                  <Typography
                    variant="inherit"
                    color="error"
                    align="left"
                    pt={1}
                    fontSize={13}
                  >
                    {errors.label_th?.message}
                  </Typography>
                </Grid>

                {/* Active checkbox */}
                <Grid item xs={12}>
                  <FormControlLabel
                    label={t("Active")}
                    control={
                      <Checkbox
                        checked={formData.is_active}
                        onChange={handleActiveChange}
                        inputProps={{ "aria-label": "Active" }}
                      />
                    }
                  />
                </Grid>
              </Grid>

              {/* Action buttons */}
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    sx={{ mt: 5, mb: 2, color: "white" }}
                    onClick={handleSubmit(onSubmit)}
                    disabled={submitLoading}
                  >
                    {isEdit ? t("Save") : t("Create")}
                    {submitLoading && (
                      <CircularProgress
                        size={24}
                        sx={{
                          color: green[500],
                          position: "absolute",
                          zIndex: 999,
                        }}
                      />
                    )}
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{ mt: 5, mb: 2 }}
                    component={ReactRouterLink}
                    to="/admin/masterdata/religion"
                  >
                    {t("Cancel")}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </>
    )
};

export default ManageReligion;
